import { Reference } from '../types';
import { getEntityCollectionActions } from './actions';
import { createHydratedEntityState, getEntityDataAdapter } from './data';
import { getEntityMetaAdapter } from './meta-adapter';
import { getSelectors } from './selectors';
import {
  GenericEntity,
  BaseSelector,
  DEFAULT_SCOPE_NAME,
  EntityState,
  GenericMetaOptions,
  GenericIdSelector,
} from './types';

export const entityInitialState: EntityState<any> = Object.freeze({
  aliases: {},
  entityMap: {},
  entityMetaMap: {},
  scope: {
    [DEFAULT_SCOPE_NAME]: [],
  },
  scopeMeta: {
    [DEFAULT_SCOPE_NAME]: {
      loaded: false,
      pending: false,
      failed: false,
      error: null,
    },
  },
  optimisticUpdate: {},
  optimisticDelete: {},
  scopePagination: {
    [DEFAULT_SCOPE_NAME]: {
      limit: null,
      page: null,
      totalCount: null,
    },
  },
});

export const createEntityAdapter = <
  Entity extends GenericEntity,
  IdSelector extends GenericIdSelector<Entity>,
  MetaOptions extends GenericMetaOptions,
>(
  baseSelector: BaseSelector<Entity, MetaOptions>,
  namespace: string,
  selectId: IdSelector,
  references?: Reference<Entity>[],
  initialCollection: Entity[] = [],
) => {
  const entityDataAdapter = getEntityDataAdapter<Entity, MetaOptions>(selectId, entityInitialState);
  const entityMetaAdapter = getEntityMetaAdapter<Entity, MetaOptions>();
  const adapter = { ...entityMetaAdapter, ...entityDataAdapter };
  const selectors = getSelectors<Entity, MetaOptions>(baseSelector, namespace, references || null);
  const defaultActionConfig = getEntityCollectionActions<Entity, IdSelector, MetaOptions>(
    adapter,
    selectId,
  );

  return {
    selectors,
    adapter,
    defaultActionConfig,
    initialState: createHydratedEntityState<Entity, MetaOptions>(
      entityInitialState,
      initialCollection,
      adapter,
    ),
  };
};
